/**
 * A chart saved as a template, and a template applied to a chart.
 *
 * Pure, and here rather than in a component for `layout-templates.ts`'s reason:
 * the interesting decisions are which fields travel and what happens to the
 * ids, and neither is testable through a surface that needs WebGL to mount.
 *
 * ## The two halves are not inverses, and must not be written as if they were
 *
 * `definitionOf` drops things. `applyTo` therefore cannot be "spread the
 * definition over the chart" — it has to say, for every field the definition
 * does *not* carry, whether the target keeps its own or gets a default. Getting
 * that backwards is how applying a template silently resets somebody's window
 * or loses their drawings.
 */

import type { Annotation, ChartGroup } from "@qufolio/model";

import { adoptPanes } from "./layout-adopt";

/**
 * What a chart contributes to a template.
 *
 * Mirrors `chart_presets.DEFINITION_KEYS` on the server, which is the
 * enforcement — this is the client saying the same thing so a save does not
 * ship a payload most of which is about to be thrown away. The server is still
 * the one that decides, because a client can be old.
 */
const TRAVELS = [
  "symbol",
  // With the symbol and only with it — see `definitionOf` and `applyTo`.
  "symbolLocked",
  "frequency",
  "barSource",
  "barsRange",
  "rightMargin",
  "xAxis",
  "gridLines",
  "axisDrag",
  "zoomAxis",
  "priceFit",
  "valueLegend",
  "projectForward",
  "panes",
] as const satisfies readonly (keyof ChartGroup)[];

/**
 * One chart, as a template definition.
 *
 * **Annotations are stripped from every pane**, which a top-level key filter
 * cannot do because they live on `Pane`. An annotation is drawn on one symbol's
 * bars at one set of prices: applied to another symbol it is a line across
 * nothing, and applied to the same symbol months later it is a line across the
 * wrong part of it. Either way it is furniture the user has to clear before the
 * template is usable.
 *
 * `keepSymbol: false` drops the symbol **entirely** rather than blanking it, so
 * "no symbol" and "the empty string" cannot be confused by a later reader.
 */
export function definitionOf(
  group: ChartGroup,
  { keepSymbol = true }: { keepSymbol?: boolean } = {},
): Partial<ChartGroup> {
  const out: Partial<ChartGroup> = {};
  for (const key of TRAVELS) {
    if (group[key] !== undefined) (out as Record<string, unknown>)[key] = group[key];
  }
  // A lock is a lock *on a symbol*: without one it would freeze whatever chart
  // the template lands on, which is the opposite of "lands on the target's".
  if (!keepSymbol) {
    delete out.symbol;
    delete out.symbolLocked;
  }
  if (Array.isArray(out.panes)) {
    out.panes = out.panes.map((pane) => {
      const { annotations: _drawn, ...rest } = pane;
      return rest;
    });
  }
  return out;
}

export interface ApplyOptions {
  /**
   * Take the template's symbol rather than keeping the chart's.
   *
   * Only meaningful when the template has one. **The caller still owns the
   * fan-out**: when the layout links `symbol`, moving it has to go through the
   * reducer's `setSymbol` so every linked chart follows, which is what the link
   * means. This function returns the group; it does not dispatch.
   */
  useTemplateSymbol?: boolean;
}

/**
 * A template written onto a chart.
 *
 * **Ids are minted, never taken from the template.** Two people applying one
 * template must not end up holding the same `ChartGroup.id`, `Pane.id` or
 * `Plot.id` — the workspace tree is addressed by them, and a collision is a
 * dispatch landing on somebody else's pane. The chart's *own* id is kept,
 * because this replaces a chart that already exists in a layout: changing it
 * would orphan every reference to the cell it sits in.
 *
 * **What the template does not carry, the chart keeps.** The window
 * (`visibleBars`), the PIP panel's placement and anything else outside
 * `TRAVELS` are properties of where this person is looking rather than of the
 * template, so they survive.
 *
 * **The chart's own drawings are kept**, which is the one thing a template must
 * not cost somebody. They are moved onto the applied chart's **price pane**,
 * because that is where they were: a drawing is a claim about price, and
 * `role: "price"` is the one pane identity that survives a change of pane count.
 * Pane two of a three-pane template is not pane two of what was there, so
 * mapping by position would scatter them.
 *
 * This is safe only because a drawing is stamped with the symbol and interval
 * it was made on and filtered on render (`annotationsFor`). Without that,
 * keeping them would carry one symbol's trendlines onto whatever the template
 * put on screen — which is the failure the stamp exists to stop.
 */
export function applyTo(
  target: ChartGroup,
  definition: Partial<ChartGroup>,
  { useTemplateSymbol = false }: ApplyOptions = {},
): ChartGroup {
  const { symbol, symbolLocked, panes, ...rest } = definition;
  const takesSymbol = Boolean(useTemplateSymbol && symbol);
  // **The lock follows the symbol** (symbol lock, owner 2026-10-06): the
  // template's symbol arrives with the template's lock, and a chart keeping its
  // own symbol keeps its own lock. Applying sets the symbol even on a locked
  // chart — the lock guards changes, not a template's definition.
  const { symbolLocked: _ownLock, ...base } = target;
  const lock = takesSymbol ? symbolLocked : target.symbolLocked;
  return {
    ...base,
    ...rest,
    ...(lock ? { symbolLocked: true } : {}),
    // Kept: this replaces a chart in a cell, and the cell refers to it by id.
    id: target.id,
    symbol: takesSymbol ? symbol! : target.symbol,
    // `adoptPanes`, not a bare re-mint: a plot names its axis by id, so the
    // ids and the refs to them have to move together or every plot is left
    // bound to nothing.
    panes: placeDrawings(
      adoptPanes(panes ?? target.panes),
      panes ? drawingsOf(target) : [],
    ),
  };
}

/** Every drawing on a chart, in pane order. */
function drawingsOf(group: ChartGroup) {
  return group.panes.flatMap((pane) => pane.annotations ?? []);
}

/**
 * Put the kept drawings on the price pane, or on the first pane if there is none.
 *
 * A fallback rather than a throw: `role` is optional in the contract, and a
 * chart whose panes carry no role is a chart somebody can still have drawn on.
 * Losing their lines to a missing field would be the worst possible reading of
 * it.
 */
function placeDrawings(panes: ChartGroup["panes"], kept: Annotation[]): ChartGroup["panes"] {
  if (kept.length === 0) return panes;
  const home = panes.findIndex((p) => p.role === "price");
  const at = home >= 0 ? home : 0;
  return panes.map((pane, i) =>
    i === at ? { ...pane, annotations: [...(pane.annotations ?? []), ...kept] } : pane,
  );
}

/**
 * How many drawings applying this template carries across.
 *
 * Nothing is lost any more — this exists so the dialog can *reassure* rather
 * than warn, which is the opposite of what it was written for and worth the
 * rename it got.
 */
export function drawingsKept(target: ChartGroup): number {
  return target.panes.reduce((n, pane) => n + (pane.annotations?.length ?? 0), 0);
}

/**
 * Whether applying this template would move more than the chart it is aimed at.
 *
 * The apply dialog says so rather than letting it happen — but **only when it
 * is true**, which is why this is a function rather than a sentence in the
 * markup. A warning that appears when nothing will happen is the kind people
 * learn to click past, and it spends the warning that matters.
 */
export function movesLinkedCharts(input: {
  useTemplateSymbol: boolean;
  templateSymbol: string | null | undefined;
  targetSymbol: string;
  symbolLinked: boolean;
  chartsInLayout: number;
}): number {
  if (!input.useTemplateSymbol || !input.templateSymbol) return 0;
  if (input.templateSymbol === input.targetSymbol) return 0;
  if (!input.symbolLinked) return 0;
  return input.chartsInLayout;
}
